import type { LocalDate } from "@js-joda/core";
import { useEffect, useState } from "react";
import type { StaffRegisterStatus } from "services/api/company/timeReports.service";
import { isOk } from "utils/result";
import type { ScheduleApiData } from "web-apps/company/components/schedule/types/schedule.types";
import { useScheduleStore } from "web-apps/company/stores/schedule/scheduleStore";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";
import { loadScheduleDayStaffStatuses } from "./scheduleDayShiftStatuses.service";

type ScheduleDayShiftsState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | {
      readonly data: ScheduleApiData;
      readonly hasPartialLoadError: boolean;
      readonly status: "ready";
    };

interface UseScheduleDayShiftsParams {
  readonly companyId: number | undefined;
  readonly date: LocalDate | null;
}

interface UseScheduleDayShiftsResult {
  readonly clockStatusByTimeReportId: ReadonlyMap<number, StaffRegisterStatus>;
  readonly state: ScheduleDayShiftsState;
}

interface UseClockStatusesParams {
  readonly companyId: number | undefined;
  readonly date: LocalDate | null;
}

const scheduleDayPageSize = 50;
const emptyClockStatuses: ReadonlyMap<number, StaffRegisterStatus> = new Map();

const useClockStatuses = ({
  companyId,
  date,
}: UseClockStatusesParams): ReadonlyMap<number, StaffRegisterStatus> => {
  const [statuses, setStatuses] = useState(emptyClockStatuses);
  useEffect(() => {
    setStatuses(emptyClockStatuses);
    if (!date || !companyId || !date.equals(getStockholmLocalDateNow())) return;
    let isActive = true;
    const load = async (): Promise<void> => {
      const result = await loadScheduleDayStaffStatuses({
        companyId,
        date,
      });
      if (isActive && isOk(result)) setStatuses(result.value);
    };
    void load();
    return () => {
      isActive = false;
    };
  }, [companyId, date]);
  return statuses;
};

export const useScheduleDayShifts = ({
  companyId,
  date,
}: UseScheduleDayShiftsParams): UseScheduleDayShiftsResult => {
  const [, scheduleActions] = useScheduleStore();
  const [state, setState] = useState<ScheduleDayShiftsState>({
    status: "idle",
  });
  const clockStatusByTimeReportId = useClockStatuses({ companyId, date });

  useEffect(() => {
    if (!date || !companyId) {
      setState({ status: "idle" });
      return;
    }
    let isActive = true;
    setState({ status: "loading" });
    const load = async (): Promise<void> => {
      const firstPage = await scheduleActions.getScheduleDay({
        companyId,
        date,
        page: 0,
        pageSize: scheduleDayPageSize,
      });
      if (!isActive) return;
      if (!isOk(firstPage)) {
        setState({ status: "error" });
        return;
      }
      const items = [...firstPage.value.items];
      const pageCount = Math.ceil(firstPage.value.total / scheduleDayPageSize);
      let hasPartialLoadError = false;
      for (let page = 1; page < pageCount; page += 1) {
        const result = await scheduleActions.getScheduleDay({
          companyId,
          date,
          page,
          pageSize: scheduleDayPageSize,
        });
        if (!isActive) return;
        if (!isOk(result)) {
          hasPartialLoadError = true;
          break;
        }
        items.push(...result.value.items);
      }
      setState({
        data: { items, total: firstPage.value.total },
        hasPartialLoadError,
        status: "ready",
      });
    };
    void load();
    return () => {
      isActive = false;
    };
  }, [companyId, date, scheduleActions]);
  return { clockStatusByTimeReportId, state };
};
